import { uiEffectsEnabled } from "../../../graphics/graphics_quality.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { NineSliceDictionary } from "../../../image_loader/nine_slicer/nine_slice_dictionary.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { SafeAreas } from "../../safe_areas.js";
import { Game } from "../../../game.js";
import { startDomSampledAnimation } from "../../dom_backend/dom_animations.js";
import { Vector2 } from "../../../common/vector2.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { formatMinutesShort } from "../../../common/reward_format.js";
import { OfferService, getPlatformProductConfig } from "../../../offers/offer_service.js";
import { IapPlugin } from "../../../plugins/iap_plugin.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import { onePlusRewardFlySources } from "./one_plus_reward_fly.js";
import type { OnePlusRewardIcon } from "./one_plus_reward_fly.js";
import { requestChainOfferStep } from "../chain_offer_screen/chain_offer_purchase_service.js";
import { getOnePlusOneState, onePlusOneAnalyticsContext, ONE_PLUS_ONE_TEMPLATE } from "../../../offers/one_plus_one_offer.js";
import type { OnePlusOneState, OnePlusOneStepState } from "../../../offers/one_plus_one_offer.js";
import type { OfferDto, OfferProductDto, OfferRewardType } from "../../../api/dto/offers.dto.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { renderElementAt } from "../../common_ui_elements/render_utils.js";
import { BackdropLayer } from "../../common_ui_elements/backdrop_layer.js";
import { TelegramAPISettings } from "../../../telegram_api_settings.js";

export interface IOnePlusOneOfferParams
{
  /** The resolved lobby_one_plus_one placement offer; null shows an empty frame that returns to the lobby. */
  offer: OfferDto | null;
  placement_id: string;
}

export interface IOnePlusOneReward
{
  type: OfferRewardType;
  amount: number;
  icon: ImgID;
  /** Rendered under the icon as-is: "300", "1h", "x3". */
  label: string;
}

/** Slots are 155 design px on the two-column mock; three columns shrink everything in proportion. */
const TWO_COLUMN_GUTTER_DESIGN = 96;
const TWO_COLUMN_GAP_DESIGN = 40;
const MAX_REWARD_SLOTS = 4;
/** The purple of the shop's free button: a hue rotation of the green art. */
export const FREE_BUTTON_HUE_ROTATION = 160;

function getRewardImgId( type: OfferRewardType ): ImgID | null
{
  switch ( type )
  {
    case "coins": return ImgID.UI_OnePlusOneOfferCoins;
    case "hearts": return ImgID.IconHeart;
    case "infinite_hearts_minutes": return ImgID.UI_OnePlusOneOfferInfiniteHeart;
    case "big_balls": return ImgID.UI_GameStartBigBallBooster;
    case "infinite_balls": return ImgID.UI_GameStartInfiniteBallsBooster;
    case "multi_balls": return ImgID.UI_GameStartMultiballsBooster;
    case "fireworks": return ImgID.UI_GameStartFireworkBooster;
    case "mines": return ImgID.UI_PassiveBoosterBomb;
    case "no_ads": case "no_ads_minutes": return ImgID.UI_RemoveAdsIcon;
    default: return null;
  }
}

function formatRewardLabel( type: OfferRewardType, amount: number ): string
{
  if ( type === "coins" ) return `${amount}`;
  if ( type === "infinite_hearts_minutes" || type === "no_ads_minutes" ) return formatMinutesShort( amount );
  return `x${amount}`;
}

/** Turns a step's reward map into icon+label rows, coins first, at most four (the column has four slots). */
export function onePlusOneRewardRows( rewards: Record<string, unknown> ): IOnePlusOneReward[]
{
  const rows: IOnePlusOneReward[] = [];
  for ( const [ type, raw ] of Object.entries( rewards ) )
  {
    const amount = typeof raw === "number" ? raw : raw === true ? 1 : 0;
    if ( amount <= 0 ) continue;
    const icon = getRewardImgId( type as OfferRewardType );
    if ( icon === null ) continue;
    rows.push( { type: type as OfferRewardType, amount, icon, label: formatRewardLabel( type as OfferRewardType, amount ) } );
  }
  rows.sort( ( a, b ) => Number( b.icon === ImgID.UI_OnePlusOneOfferCoins ) - Number( a.icon === ImgID.UI_OnePlusOneOfferCoins ) );
  return rows.slice( 0, MAX_REWARD_SLOTS );
}

export class OnePlusOneOfferScreen extends UIElement
{
  private static readonly DESIGN_W = 1080;
  private static readonly DESIGN_H = 1920;
  private static readonly TITLE_SCALE = 0.9;

  private readonly params: IOnePlusOneOfferParams;
  private readonly body: UIElementHolder;
  /** Columns are rebuilt after every purchase/claim; they live in their own holder so the frame stays. */
  private readonly columns: UIElementHolder;
  private readonly body_w: number;
  private readonly body_h: number;

  private offer: OfferDto | null;
  private state: OnePlusOneState | null = null;
  private busy = false;
  private completed = false;
  /** Reward icons per step id, so a claim can fly exactly what the column showed. */
  private readonly reward_icons = new Map<string, OnePlusRewardIcon[]>();
  private price_text: string | null = null;
  private price_requested_for: string | null = null;

  private events_controller: BasicEventsController | null = null;
  private background_gradient: CanvasGradient | null = null;
  private exiting = false;

  private timer_text: UITextElement | null = null;
  private timer_deadline_ms = 0;
  private has_timer = false;
  private timer_last_shown = "";
  private lock_icons = new Map<number, UIImage>();
  private lock_animating_index = -1;
  private lock_anim_start = 0;

  private confetti_layers: UIImage[] = [];
  private confetti_base_positions: Vector2[] = [];
  private confetti_pos = new Vector2( 0, 0 );
  private confetti_started_at = 0;
  private confetti_painted_at = 0;

  // Gradient, sky, frame and confetti live on a backdrop canvas under the
  // screen canvas; the screen canvas holds only the content, transparent
  // elsewhere, and is repainted only when the content changes.
  private sky: UIImage | null = null;
  private frame_halves: UIElement[] = [];
  private backdrop: BackdropLayer | null = null;
  // The first render can come before the first update(), which then moves
  // every element to its render position and repaints it all a frame later.
  private updated_once = false;

  private static readonly LOCK_ANIM_DURATION_MS = 1000;
  private static readonly LOCK_WOBBLE_SPEED = 0.05;
  private static readonly LOCK_WOBBLE_MAX_ANGLE = 0.2;

  private static readonly CONFETTI_LAYER_COUNT = 2;
  private static readonly CONFETTI_CYCLE_MS = 15000;
  private static readonly CONFETTI_FALL_DESIGN = 1200;
  private static readonly CONFETTI_SWAY_DESIGN = 24;
  private static readonly CONFETTI_LAYER_SCALE_STEP = 0.14;
  /** The sheets drift ~30 css px/s, so 60 Hz is visually identical and halves the blits on 120 Hz screens. */
  private static readonly CONFETTI_FRAME_MS = 1000 / 60 - 1;

  private static readonly TIGER_ASPECT = 688 / 739;
  private static readonly TELEGRAM_TIGER_SHIFT = 72;

  private getCardLayout(): { x: number; width: number; center_x: number }
  {
    const center_x = this.body_w / 2;
    const width = LayoutScaler.widthFromDesign( 990 );

    return { x: center_x - width / 2, width, center_x };
  }

  private getBodyScreenTop(): number
  {
    return -Game.safeAreaTop() - ( Game.getContentHeight() - this.body_h );
  }

  private getBlockTopShift(): number
  {
    const screen_top = this.getBodyScreenTop();
    const close_top = LayoutScaler.yFromDesign( -33 );
    const margin = LayoutScaler.yFromDesign( 24 );

    return Math.max( 0, screen_top + margin - close_top );
  }

  public constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: IOnePlusOneOfferParams
  )
  {
    super( ctx, x, y, width, height, false, false );

    this.params = params;
    this.offer = params.offer;
    this.state = getOnePlusOneState( this.offer );
    this.primePriceFromCache();

    this.body_w = LayoutScaler.widthFromDesign( OnePlusOneOfferScreen.DESIGN_W );
    this.body_h = LayoutScaler.heightFromDesign( OnePlusOneOfferScreen.DESIGN_H );

    this.body = new UIElementHolder(
      ctx,
      ( width - this.body_w ) / 2,
      height - this.body_h,
      this.body_w,
      this.body_h
    );
    this.columns = new UIElementHolder( ctx, 0, 0, this.body_w, this.body_h );

    this.initializeEventsController( ctx.canvas );

    // The countdown is the offer's own end (server clock), not a local guess.
    const remaining_ms = this.offer ? this.remainingOfferMs() : null;
    this.has_timer = remaining_ms !== null && remaining_ms > 0;
    if ( this.has_timer )
      this.timer_deadline_ms = performance.now() + ( remaining_ms ?? 0 );

    this.createElements();

    if ( this.offer )
      void OfferService.trackShown( this.offer.id, this.analyticsContext() );
  }

  private analyticsContext( chain_step_id?: string ): Record<string, unknown>
  {
    return onePlusOneAnalyticsContext( this.params.placement_id, chain_step_id );
  }

  private titleKey(): string
  {
    return this.offer?.texts?.title_key ?? "OFFER.ONE_PLUS_ONE.TITLE";
  }

  private subtitleKey(): string
  {
    return this.offer?.texts?.description_key ?? "OFFER.ONE_PLUS_ONE.SUBTITLE";
  }

  /** Milliseconds until the offer ends by the server clock; null when it has no end. */
  private remainingOfferMs(): number | null
  {
    if ( !this.offer ) return null;
    const deadline_ms = OfferService.getOfferDisplayEndMs( this.offer );
    if ( deadline_ms === null ) return null;
    const server_now_ms = OfferService.getServerNowMs() ?? Date.now();
    return deadline_ms - server_now_ms;
  }

  private createElements(): void
  {
    this.createSky();
    this.addChild( this.body );

    this.createFrame();
    this.createArt();
    this.createTitle();
    this.createSubtitle();
    this.createTimer();
    this.body.addChild( this.columns );
    this.buildColumns();
    this.createCloseIcon();
  }

  private createSky(): void
  {
    const screen_left = -Game.safeAreaLeft();
    const screen_top = -Game.safeAreaTop();

    const sky_w = Math.max( LayoutScaler.widthFromDesign( 1440 ), Game.screenW() );
    const sky_h = Math.round( sky_w * 921 / 1440 );
    const sky_x = Math.max(
      screen_left + Game.screenW() - sky_w,
      Math.min( ( this.body_w - sky_w ) / 2, screen_left )
    );

    const sky = new UIImage(
      this.ctx,
      sky_x,
      screen_top + LayoutScaler.yFromDesign( -71 ),
      sky_w,
      sky_h,
      ImgID.UI_OnePlusOneOfferSky
    );
    // Painted on the backdrop, so kept out of the children (parent for placement only).
    if ( this.dom_mode )
      this.addChild( sky );
    else
      sky.setParentElement( this );
    this.sky = sky;
  }

  private createFrame(): void
  {
    const card_y = LayoutScaler.yFromDesign( -16.1 ) + this.getBlockTopShift();
    const card_h = LayoutScaler.heightFromDesign( 1899 ) - card_y;
    const card = this.getCardLayout();
    const half_w = card.width / 2;

    const left_half = new UINineSliceElement(
      this.ctx,
      card.x,
      card_y,
      half_w,
      card_h,
      ImgID.UI_OnePlusOneOfferFrame,
      NineSliceDictionary.values.one_plus_one_frame_half
    );
    if ( this.dom_mode )
      this.body.addChild( left_half );
    else
      left_half.setParentElement( this.body );

    const right_half = new UINineSliceElement(
      this.ctx,
      card.center_x,
      card_y,
      half_w,
      card_h,
      ImgID.UI_OnePlusOneOfferFrame,
      NineSliceDictionary.values.one_plus_one_frame_half
    );
    right_half.setFlipX( true );
    if ( this.dom_mode )
      this.body.addChild( right_half );
    else
      right_half.setParentElement( this.body );
    this.frame_halves = [ left_half, right_half ];
  }

  private createArt(): void
  {
    this.createConfetti();
    const tiger_h = LayoutScaler.heightFromLayouts( 347, 646 );
    const tiger_w = tiger_h * OnePlusOneOfferScreen.TIGER_ASPECT;
    const tiger_x = this.getCardLayout().center_x - tiger_w / 2;
    const telegram_shift = TelegramAPISettings.isTelegramEnvironment()
      ? LayoutScaler.yFromDesign( OnePlusOneOfferScreen.TELEGRAM_TIGER_SHIFT )
      : 0;

    const tiger = new UIImage(
      this.ctx,
      tiger_x,
      LayoutScaler.yFromLayouts( 0, -382 ) + telegram_shift,
      tiger_w,
      tiger_h,
      ImgID.UI_OnePlusOneOfferTiger
    );
    this.body.addChild( tiger );
  }

  private createConfetti(): void
  {
    const sheet_w = LayoutScaler.widthFromDesign( 1247 );
    const sheet_h = LayoutScaler.heightFromDesign( 1704 );
    const sheet_x = LayoutScaler.xFromDesign( -35.8 );
    const sheet_y = LayoutScaler.yFromLayouts( -360, -566 );

    this.confetti_started_at = performance.now();

    for ( let layer = 0; layer < OnePlusOneOfferScreen.CONFETTI_LAYER_COUNT; ++layer )
    {
      const scale = 1 + layer * OnePlusOneOfferScreen.CONFETTI_LAYER_SCALE_STEP;
      const width = sheet_w * scale;
      const height = sheet_h * scale;
      const base = new Vector2( sheet_x - ( width - sheet_w ) / 2, sheet_y - ( height - sheet_h ) / 2 );

      const sheet = new UIImage( this.ctx, base.x, base.y, width, height, ImgID.UI_OnePlusOneOfferConfetti );
      sheet.setFlipX( layer % 2 === 1 );

      // Parented for positioning only: kept out of the children so its
      // per-frame motion does not mark the whole tree dirty.
      if ( this.dom_mode )
        this.body.addChild( sheet );
      else
        sheet.setParentElement( this.body );
      this.confetti_layers.push( sheet );
      this.confetti_base_positions.push( base );
    }

    this.layoutConfetti( this.confetti_started_at );
    this.startConfettiDomLoop();
  }

  private confetti_anims: Animation[] = [];
  private lock_anim: Animation | null = null;

  private startConfettiDomLoop(): void
  {
    if ( !this.dom_mode )
      return;

    if ( !uiEffectsEnabled() )
      return;

    const cycle = OnePlusOneOfferScreen.CONFETTI_CYCLE_MS;
    const fall = LayoutScaler.heightFromDesign( OnePlusOneOfferScreen.CONFETTI_FALL_DESIGN );
    const sway = LayoutScaler.widthFromDesign( OnePlusOneOfferScreen.CONFETTI_SWAY_DESIGN );
    const count = this.confetti_layers.length;

    for ( let layer = 0; layer < count; ++layer )
    {
      const base = this.confetti_base_positions[ layer ];
      const anim = startDomSampledAnimation( this.confetti_layers[ layer ].getDomNode(), cycle, t =>
      {
        const phase = ( t + layer / count ) % 1;
        return {
          pos: new Vector2( base.x + Math.sin( phase * Math.PI * 2 ) * sway, base.y + ( phase - 0.5 ) * fall ),
          opacity: OnePlusOneOfferScreen.confettiOpacity( phase ),
        };
      }, { iterations: Infinity, samples: 120 } );
      if ( anim )
        this.confetti_anims.push( anim );
    }
  }

  private cancelDomAnims(): void
  {
    for ( const anim of this.confetti_anims )
      anim.cancel();
    this.confetti_anims = [];
    this.lock_anim?.cancel();
    this.lock_anim = null;
  }

  private layoutConfetti( now: number ): void
  {
    const cycle = OnePlusOneOfferScreen.CONFETTI_CYCLE_MS;
    const fall = LayoutScaler.heightFromDesign( OnePlusOneOfferScreen.CONFETTI_FALL_DESIGN );
    const sway = LayoutScaler.widthFromDesign( OnePlusOneOfferScreen.CONFETTI_SWAY_DESIGN );
    const elapsed = now - this.confetti_started_at;
    const count = this.confetti_layers.length;

    for ( let layer = 0; layer < count; ++layer )
    {
      const phase = ( elapsed / cycle + layer / count ) % 1;
      const base = this.confetti_base_positions[ layer ];
      this.confetti_pos.set(
        base.x + Math.sin( phase * Math.PI * 2 ) * sway,
        base.y + ( phase - 0.5 ) * fall
      );

      this.confetti_layers[ layer ].setPixelPos( this.confetti_pos, true );
      this.confetti_layers[ layer ].setOpacityImmediate( OnePlusOneOfferScreen.confettiOpacity( phase ) );
    }
  }

  private updateConfetti( now: number ): void
  {
    if ( this.confetti_layers.length === 0 || this.dom_mode )
      return;

    this.layoutConfetti( now );
  }


  private static confettiOpacity( phase: number ): number
  {
    return OnePlusOneOfferScreen.smoothStep( 1 - Math.abs( 2 * phase - 1 ) );
  }

  private static smoothStep( t: number ): number
  {
    const clamped = Math.max( 0, Math.min( 1, t ) );

    return clamped * clamped * ( 3 - 2 * clamped );
  }

  private createTitle(): void
  {
    this.addTitleOutline( 307, 42, "#6C1F04" );
    this.addTitleOutline( 304, 38, "#F9AF26" );
    this.addTitleOutline( 304, 28, "#4A0224" );
    this.addTitleOutline( 301, 22, "#E95600" );
    this.addTitleOutline( 301, 6, "#AA6600" );

    const card = this.getCardLayout();
    const title_w = card.width * OnePlusOneOfferScreen.TITLE_SCALE;
    const title_x = card.x + ( card.width - title_w ) / 2;
    const title_y = LayoutScaler.yFromLayouts( 301, 183 ) + LayoutScaler.widthFromLayouts( 0, -45 );
    const title_h = LayoutScaler.heightFromDesign( 183 );

    const fill = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        localization_key: this.titleKey(),
        font_size: LayoutScaler.textSizeFromDesign( 130 * OnePlusOneOfferScreen.TITLE_SCALE ),
        text_style: TextStyleScheme.geologica,
        text_color: "#FCEA3E",
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 80 * OnePlusOneOfferScreen.TITLE_SCALE ),
      }
    );
    this.body.addChild( fill );
  }

  private addTitleOutline( design_y: number, outline_width: number, outline_color: string ): void
  {
    const card = this.getCardLayout();
    const title_w = card.width * OnePlusOneOfferScreen.TITLE_SCALE;
    const title_x = card.x + ( card.width - title_w ) / 2;
    const title_y = LayoutScaler.yFromLayouts( design_y, design_y - 118 ) + LayoutScaler.widthFromLayouts( 0, -45 );
    const title_h = LayoutScaler.heightFromDesign( 183 );

    const layer = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        localization_key: this.titleKey(),
        font_size: LayoutScaler.textSizeFromDesign( 130 * OnePlusOneOfferScreen.TITLE_SCALE ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        outline_width: LayoutScaler.textBorderSizeFromDesign( outline_width * OnePlusOneOfferScreen.TITLE_SCALE ),
        outline_color: outline_color,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 80 * OnePlusOneOfferScreen.TITLE_SCALE ),
      }
    );
    this.body.addChild( layer );
  }

  private createSubtitle(): void
  {
    const card = this.getCardLayout();

    const subtitle = new UITextElement(
      this.ctx,
      card.x,
      LayoutScaler.yFromLayouts( 466.6, 353 ),
      card.width,
      LayoutScaler.heightFromDesign( 124 ),
      {
        localization_key: this.subtitleKey(),
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 800,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: "#021A67",
        line_height: 1.1,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 34 ),
      }
    );
    this.body.addChild( subtitle );
  }

  private createTimer(): void
  {
    if ( !this.has_timer )
      return;

    const timer_y = LayoutScaler.yFromLayouts( 622.1, 495 );
    const timer_h = LayoutScaler.heightFromDesign( 77.9 );

    const substrate = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 396 ),
      timer_y,
      LayoutScaler.widthFromDesign( 287.9 ),
      timer_h,
      ImgID.UI_OnePlusOneOfferTimerBg
    );
    this.body.addChild( substrate );

    this.timer_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 466 ),
      timer_y,
      LayoutScaler.widthFromDesign( 204 ),
      timer_h,
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        center_on_cap_height: true,
        ...TextStyleScheme.timerShadow(),
        font_weight: 900,
      }
    );
    this.body.addChild( this.timer_text );

    this.updateTimer( performance.now() );
  }



  // ---- Columns: one per chain step, rebuilt whenever the server cursor moves.

  private buildColumns(): void
  {
    this.columns.removeAllChildren();
    this.lock_icons.clear();
    this.reward_icons.clear();
    this.lock_animating_index = -1;

    const steps = this.state?.steps ?? [];
    if ( !steps.length )
      return;

    const card = this.getCardLayout();
    const count = steps.length;
    // The mock is drawn for two columns; more columns share the same card
    // width, so every column (and what is inside it) scales down in proportion.
    const gutter = LayoutScaler.widthFromDesign( TWO_COLUMN_GUTTER_DESIGN );
    const gap = LayoutScaler.widthFromDesign( TWO_COLUMN_GAP_DESIGN );
    const two_column_w = ( card.width - gutter * 3 ) / 2;
    const column_w = ( two_column_w * 2 + gap - gap * ( count - 1 ) ) / count;
    const scale = column_w / two_column_w;
    const total_w = column_w * count + gap * ( count - 1 );
    const first_x = card.center_x - total_w / 2;

    for ( let index = 0; index < count; ++index )
    {
      const column_x = first_x + index * ( column_w + gap );
      this.createColumn( column_x, column_w, scale, steps[ index ] );
      if ( index > 0 )
        this.createPlus( column_x - gap / 2, scale );
    }

    this.invalidateSubtree();
    this.forceMakeShouldBeRendered();
  }

  private createColumn( column_x: number, column_w: number, scale: number, state: OnePlusOneStepState ): void
  {
    const tall_block_shift = LayoutScaler.yFromLayouts( 0, 390 );
    const wide_strip_shift = LayoutScaler.widthFromLayouts( 0, -35 );
    const panel_y = LayoutScaler.yFromLayouts( 719, 227 ) + tall_block_shift;
    const panel_bottom = LayoutScaler.yFromLayouts( 1797.9, 1410 ) + tall_block_shift;
    const panel_h = panel_bottom - panel_y;

    const panel = new RoundedRectangle(
      this.ctx,
      column_x,
      panel_y,
      column_w,
      panel_h,
      LayoutScaler.cornerRadiusFromDesign( 54 * scale ),
      {
        type: "linear-gradient",
        direction: "vertical",
        stops: [
          { offset: 0, color: "rgba(9, 94, 186, 0.161)" },
          { offset: 1, color: "#65D6FE" },
        ],
      }
    );
    this.columns.addChild( panel );

    const strip_inset = LayoutScaler.widthFromDesign( 57 * scale );
    const strip = new RoundedRectangle(
      this.ctx,
      column_x + strip_inset,
      LayoutScaler.yFromLayouts( 774, 274 ) + tall_block_shift + wide_strip_shift,
      column_w - strip_inset * 2,
      LayoutScaler.heightFromLayouts( 831, 920 ),
      LayoutScaler.cornerRadiusFromDesign( 34 * scale ),
      "#0446AE",
      undefined,
      false,
      undefined,
      0,
      {
        color: "rgba(255, 255, 255, 0.678)",
        blur: LayoutScaler.widthFromDesign( 6 ),
        offsetY: LayoutScaler.yFromDesign( 3 ),
      }
    );
    this.columns.addChild( strip );

    const slot_y = [
      LayoutScaler.yFromLayouts( 774, 260 ) + tall_block_shift + wide_strip_shift,
      LayoutScaler.yFromLayouts( 984, 495 ) + tall_block_shift + wide_strip_shift,
      LayoutScaler.yFromLayouts( 1194, 730 ) + tall_block_shift + wide_strip_shift,
      LayoutScaler.yFromLayouts( 1404, 965 ) + tall_block_shift + wide_strip_shift,
    ];
    const rewards_y_offset = LayoutScaler.yFromLayouts( 0, 22 );
    const rewards = onePlusOneRewardRows( state.step.rewards as unknown as Record<string, unknown> );
    const slot_count = Math.min( rewards.length, slot_y.length );

    const icons: OnePlusRewardIcon[] = [];
    for ( let slot = 0; slot < slot_count; ++slot )
      icons.push( { reward: rewards[ slot ], icon: this.createReward( column_x, column_w, scale, slot_y[ slot ] + rewards_y_offset, rewards[ slot ], state.status === 'claimed' ) } );
    this.reward_icons.set( state.step.id, icons );

    this.createButton( column_x, column_w, scale, panel_bottom, state );
  }

  private createReward(
    column_x: number,
    column_w: number,
    scale: number,
    slot_y: number,
    reward: IOnePlusOneReward,
    claimed: boolean
  ): UIImage
  {
    const icon_size = 155 * scale;
    const label_pad = 30 * scale;
    const icon_w = LayoutScaler.widthFromDesign( icon_size );
    const icon_x = column_x + ( column_w - icon_w ) / 2;
    const icon_y = slot_y;

    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      LayoutScaler.heightFromDesign( icon_size ),
      reward.icon
    );
    if ( claimed )
      icon.setDimmed( true );
    this.columns.addChild( icon );

    const label = new UITextElement(
      this.ctx,
      icon_x - LayoutScaler.widthFromDesign( label_pad ),
      icon_y + LayoutScaler.yFromDesign( 114.2 * scale ),
      LayoutScaler.widthFromDesign( icon_size + label_pad * 2 ),
      LayoutScaler.heightFromDesign( 69 * scale ),
      {
        text: reward.label,
        font_size: LayoutScaler.textSizeFromDesign( 50 * scale ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 9 * scale ),
        outline_color: "#003F87",
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 32 * scale ),
      }
    );
    this.columns.addChild( label );
    RewardTooltip.attach( icon, reward.type, [ label ] );
    return icon;
  }

  private createButton( column_x: number, column_w: number, scale: number, panel_bottom: number, state: OnePlusOneStepState ): void
  {
    const art_w = 332;
    const art_h = 185;
    const face_w = 290;
    const face_h = 143;
    const glow = 21;

    const inset = LayoutScaler.widthFromDesign( 30 * scale );
    const btn_w = ( column_w - inset * 2 ) * art_w / face_w;
    const btn_h = LayoutScaler.heightFromDesign( art_h * scale );
    const btn_x = column_x + inset - btn_w * glow / art_w;
    const btn_y = panel_bottom - inset - LayoutScaler.heightFromDesign( ( face_h + glow ) * scale );

    const button = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_OnePlusOneOfferGreenBtn,
      {},
      true
    );
    // Keep locked rewards green so the regular coloured lock remains intact.
    // Only rewards that were already collected are greyed out.
    if ( state.status === 'claimed' )
      button.setDimmed( true );
    else if ( state.status === 'claimable' )
      button.setHueRotation( FREE_BUTTON_HUE_ROTATION );
    button.setOnClick( () => { void this.handleStepClick( state ); } );
    this.columns.addChild( button );

    // The art carries a transparent glow on both sides; fit the caption to the
    // visible face minus a margin, or long captions spill past the button edge.
    const caption_pad = 22;
    const caption = new UITextElement(
      this.ctx,
      btn_w * ( glow + caption_pad ) / art_w,
      0,
      btn_w * ( face_w - caption_pad * 2 ) / art_w,
      btn_h,
      {
        text: this.getButtonCaption( state ),
        font_size: LayoutScaler.textSizeFromDesign( ( state.status === 'paid' ? 50 : 60 ) * scale ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        ...TextStyleScheme.buttonShadow(),
        outline_color: state.status === 'claimed' ? ColorScheme.non_active_btn_outline
          : state.status === 'claimable' ? ColorScheme.more_lives_purple_outline : ColorScheme.more_lives_buy_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 * scale ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 * scale ),
        font_weight: 800,
        line_height: 1.04,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 24 * scale ),
      }
    );
    button.addChild( caption, true );

    if ( state.status !== 'locked' )
      return;

    const lock_w = LayoutScaler.widthFromDesign( 96.7 * scale );
    const lock_h = LayoutScaler.heightFromDesign( 105.2 * scale );
    const lock_icon = new UIImage(
      this.ctx,
      btn_w * 287.75 / art_w - lock_w / 2,
      btn_h * 35.05 / art_h - lock_h / 2,
      lock_w,
      lock_h,
      ImgID.UI_OnePlusOneOfferLock
    );
    lock_icon.enableRotation();
    button.addChild( lock_icon, true );
    this.lock_icons.set( state.index, lock_icon );
  }

  private getButtonCaption( state: OnePlusOneStepState ): string
  {
    switch ( state.status )
    {
      case 'paid':
        return this.price_text ?? '…';
      case 'claimed':
        return LocalizationManager.t( "OFFER.ONE_PLUS_ONE.CLAIMED" );
      default:
        return LocalizationManager.t( this.offer?.texts?.button_key ?? "OFFER.ONE_PLUS_ONE.FREE" );
    }
  }

  /** The store price is fetched once per paid step; the button reads "…" until it lands. */
  /**
   * Uses a price fetched earlier this session (prepareScreenImages asks the
   * store before the screen opens), so the paid button is built with it
   * instead of '…' and the columns are not rebuilt a few frames in.
   */
  private primePriceFromCache(): void
  {
    const paid = this.state?.steps.find( item => item.status === 'paid' );
    const product = paid?.step.products ? getPlatformProductConfig( paid.step.products ) : null;
    const price = product ? IapPlugin.peekProductDetails( product.productId )?.formatted_price : undefined;
    if ( !this.offer || !paid || !product || !price ) return;

    this.price_text = price;
    this.price_requested_for = paid.step.id;
    void OfferService.trackPriceLoaded( this.offer.id, { ...this.analyticsContext( paid.step.id ), productid: product.productId } );
  }

  private loadPrice( state: OnePlusOneStepState ): void
  {
    if ( !this.offer || this.price_requested_for === state.step.id ) return;
    const product = state.step.products ? this.platformProduct( state.step.products ) : null;
    if ( !product ) return;
    this.price_requested_for = state.step.id;
    const offer_id = this.offer.id;
    const context = { ...this.analyticsContext( state.step.id ), productid: product.productId };
    void ( async () =>
    {
      try
      {
        const details = await IapPlugin.getProductDetails( product.productId );
        this.price_text = details?.formatted_price ?? 'Buy';
        await OfferService.trackPriceLoaded( offer_id, context );
      }
      catch
      {
        this.price_text = 'Buy';
        await OfferService.trackPriceUnavailable( offer_id, context );
      }
      if ( !this.exiting ) this.buildColumns();
    } )();
  }

  private platformProduct( products: NonNullable<OnePlusOneStepState['step']['products']> ): OfferProductDto | null
  {
    return getPlatformProductConfig( products );
  }

  private async handleStepClick( state: OnePlusOneStepState ): Promise<void>
  {
    if ( this.exiting || !this.offer ) return;
    if ( state.status === 'locked' )
    {
      this.wobbleLock( state.index );
      return;
    }
    if ( state.status === 'claimed' || this.busy ) return;

    void audioController.playSound( AudioClips.UI_CLICK );
    this.busy = true;
    const stats_before = StatisticsStorage.load();
    const result = await requestChainOfferStep(
      this.offer.id,
      state.step.id,
      state.status === 'paid' ? 'paid' : 'free',
      { placement_id: this.params.placement_id, placement: this.params.placement_id, template: ONE_PLUS_ONE_TEMPLATE },
    );
    this.busy = false;
    if ( this.exiting ) return;

    // The service refreshed the catalog: re-read the cursor. A finished
    // chain is no longer listed at all, which is the completion signal.
    const fresh = OfferService.getOffer( this.offer.id );
    const fresh_state = getOnePlusOneState( fresh );
    const fly_sources = result === 'claimed' ? onePlusRewardFlySources( this.reward_icons.get( state.step.id ) ?? [] ) : [];
    if ( result === 'claimed' && ( !fresh_state || fresh_state.all_claimed ) )
    {
      // Everything is taken: let the rewards land in the HUD, then leave.
      this.completed = true;
      this.busy = true;
      const fly_end = RewardFlyToHud.play( this.ctx, this, fly_sources, stats_before );
      window.setTimeout( () => this.exitToMenu( "completed" ), Math.max( 0, fly_end - performance.now() ) );
      return;
    }
    if ( !fresh || !fresh_state )
    {
      this.exitToMenu( "offer_gone" );
      return;
    }
    this.offer = fresh;
    this.state = fresh_state;
    this.buildColumns();
    if ( fly_sources.length )
      RewardFlyToHud.play( this.ctx, this, fly_sources, stats_before );
  }

  private wobbleLock( index: number ): void
  {
    if ( this.lock_animating_index !== -1 || !this.lock_icons.has( index ) )
      return;

    void audioController.playSound( AudioClips.UI_CLICK );

    this.lock_anim_start = performance.now();
    this.lock_animating_index = index;
    const icon = this.lock_icons.get( index );
    icon?.setRotationImmediate( 0 );
    if ( icon && this.dom_mode )
    {
      const duration = OnePlusOneOfferScreen.LOCK_ANIM_DURATION_MS;
      this.lock_anim?.cancel();
      this.lock_anim = startDomSampledAnimation( icon.getDomNode(), duration, t =>
      {
        const elapsed = t * duration;
        const envelope = Math.sin( t * Math.PI );
        return { rotation: Math.sin( elapsed * OnePlusOneOfferScreen.LOCK_WOBBLE_SPEED ) * OnePlusOneOfferScreen.LOCK_WOBBLE_MAX_ANGLE * envelope };
      }, { samples: 60 } );
    }
    this.forceMakeShouldBeRendered();
  }

  private updateLockAnimation( now: number ): void
  {
    const lock_icon = this.lock_animating_index === -1 ? null : this.lock_icons.get( this.lock_animating_index ) ?? null;
    if ( lock_icon === null )
      return;

    const elapsed = now - this.lock_anim_start;
    const duration = OnePlusOneOfferScreen.LOCK_ANIM_DURATION_MS;

    if ( elapsed >= duration )
    {
      this.lock_animating_index = -1;
      lock_icon.setRotationImmediate( 0 );
      this.forceMakeShouldBeRendered();
      return;
    }

    if ( this.dom_mode )
      return;

    const envelope = Math.sin( elapsed / duration * Math.PI );
    const angle = Math.sin( elapsed * OnePlusOneOfferScreen.LOCK_WOBBLE_SPEED )
      * OnePlusOneOfferScreen.LOCK_WOBBLE_MAX_ANGLE * envelope;
    lock_icon.setRotationImmediate( angle );
    this.forceMakeShouldBeRendered();
  }

  private createPlus( center_x: number, scale: number ): void
  {
    const plus_size = LayoutScaler.widthFromDesign( 120 * scale );
    const plus_x = center_x - plus_size / 2;
    const plus_y = LayoutScaler.yFromLayouts( 1198, 1113 ) - plus_size / 2;

    const plus = new UIImage(
      this.ctx,
      plus_x,
      plus_y,
      plus_size,
      plus_size,
      ImgID.UI_OnePlusOneOfferPlus
    );
    this.columns.addChild( plus );
  }

  private createCloseIcon(): void
  {
    const icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 917 ),
      LayoutScaler.yFromDesign( -33 ) + this.getBlockTopShift(),
      LayoutScaler.widthFromDesign( 110 ),
      LayoutScaler.heightFromDesign( 110 ),
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    icon.setOnClick( () => this.exitToMenu( "user_close" ) );

    this.body.addChild( icon );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) => { super.onTouchStart( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchEnd( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchMove( p, performance.now() ); },
      true
    );
  }

  private updateTimer( now: number ): void
  {
    if ( !this.has_timer || this.timer_text === null )
      return;

    const remaining_ms = this.timer_deadline_ms - now;
    // Once bought, the offer stays until the player takes every free step.
    if ( remaining_ms <= 0 && !this.exiting && this.state?.awaiting_purchase !== false )
    {
      // The offer has ended: the server will not list it any more, so leave
      // (taking any free step already unlocked with us, see exitToMenu).
      this.exitToMenu( "expired" );
      return;
    }

    const text = formatShortDuration( Math.max( 0, remaining_ms ) );

    if ( text === this.timer_last_shown )
      return;

    this.timer_last_shown = text;
    this.timer_text.setText( text );
    this.invalidateSubtree();
  }

  private exitToMenu( reason: string ): void
  {
    if ( this.exiting )
      return;

    this.exiting = true;

    if ( this.offer )
    {
      void OfferService.trackClosed( this.offer.id, { ...this.analyticsContext(), reason } );
    }

    void ScreenManager.showScreen( Screens.MENU );
  }

  public destroy(): void
  {
    this.cancelDomAnims();
    this.exiting = true;
    this.lock_animating_index = -1;
    for ( const lock_icon of this.lock_icons.values() )
      lock_icon.setRotationImmediate( 0 );
    this.lock_icons.clear();
    this.confetti_layers = [];
    this.confetti_base_positions = [];
    this.backdrop?.destroy();
    this.backdrop = null;

    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
  }

  public update( now: number ): void
  {
    this.updated_once = true;
    this.updateBasic( now );
    if ( !this.offer && !this.exiting )
    {
      // Nothing to show (opened without a resolved placement): back to the lobby.
      this.exitToMenu( "offer_gone" );
      return;
    }
    const paid = this.state?.steps.find( item => item.status === 'paid' );
    if ( paid && this.price_text === null )
      this.loadPrice( paid );
    this.updateTimer( now );
    this.updateLockAnimation( now );
    this.updateConfetti( now );
  }

  private getBackgroundGradient(): CanvasGradient
  {
    if ( !this.background_gradient )
    {
      const gradient = this.ctx.createLinearGradient( 0, 0, 0, Game.screenH() );
      gradient.addColorStop( 0, "#3C1D6E" );
      gradient.addColorStop( 1, "#013F87" );
      this.background_gradient = gradient;
    }

    return this.background_gradient;
  }

  public render(): void
  {
    const now = performance.now();
    if ( !this.updated_once ) this.update( now );
    if ( !this.backdrop && !this.exiting && !this.dom_mode )
    {
      this.backdrop = BackdropLayer.under( this.ctx.canvas );
      this.confetti_painted_at = 0;
    }

    if ( this.backdrop && now - this.confetti_painted_at >= OnePlusOneOfferScreen.CONFETTI_FRAME_MS )
    {
      this.confetti_painted_at = now;
      const back = this.backdrop.ctx;
      back.fillStyle = this.getBackgroundGradient();
      back.fillRect( 0, 0, Game.screenW(), Game.screenH() );
      if ( this.sky ) renderElementAt( back, this.sky );
      for ( const half of this.frame_halves )
        renderElementAt( back, half );
      for ( const sheet of this.confetti_layers )
        renderElementAt( back, sheet );
    }

    if ( !this.shouldBeRendered() )
      return;

    if ( this.dom_mode )
    {
      this.setDomBackdrop( "linear-gradient(#3C1D6E, #013F87)" );
      this.startRender();
      this.finishRender();
      SafeAreas.syncBothSafeAreaOverlaysDom( this.getDomHost() );
      return;
    }

    const ctx = this.ctx;
    ctx.save();
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    ctx.clearRect( 0, 0, ctx.canvas.width, ctx.canvas.height );
    ctx.restore();
    this.startRender();
    this.finishRender();
    SafeAreas.renderBothSafeAreaOverlays( ctx );
  }

  public name(): string
  {
    return "OnePlusOneOfferScreen";
  }
}
